import { useCallback } from 'react';
import { MRTFilter, mrtFiltersToPrismaWhere, SubFilters } from '../services/mrtFilterService';

/**
 * Interface mínima que debe cumplir el store de listado para usar este hook
 */
export interface GridFilter {
  columnName: string;
  operation?: string;
  value?: any;
}

export interface DataGridListStore {
  grid: {
    setMrtColumnVisibility: (visibility: Record<string, boolean>) => void;
    setColumnOrder: (order: string[]) => void;
    setMrtColumnSizing: (sizing: Record<string, number>) => void;
    setSorting: (sorting: { columnName: string; direction: 'asc' | 'desc' }[]) => void;
    setSelectedFilters: (filters: GridFilter[]) => void;
    subFilters?: SubFilters;
    columnFilterAlias?: Record<string, string>;
  };
  whereFilters?: any;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  debounceLoadFirstPage: () => void;
  loadFirstPage: (clear: boolean) => void;
}

/**
 * Interface mínima que debe cumplir el rootStore
 */
export interface DataGridRootStore {
  langStore: {
    timeZone: string;
  };
}

/**
 * Hook que proporciona los handlers comunes para DataGrid.
 * Estos handlers son idénticos en todas las entidades, por lo que se extraen aquí.
 */
export function useDataGridHandlers<T extends DataGridListStore>(
  store: T,
  rootStore: DataGridRootStore
) {
  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      store.grid.setMrtColumnVisibility(visibility);
    },
    [store.grid]
  );

  const handleColumnOrderChange = useCallback(
    (order: string[]) => {
      store.grid.setColumnOrder(order);
    },
    [store.grid]
  );

  const handleColumnSizingChange = useCallback(
    (sizing: Record<string, number>) => {
      store.grid.setMrtColumnSizing(sizing);
    },
    [store.grid]
  );

  const handleFiltersChange = useCallback(
    (filters: MRTFilter[]) => {
      const timeZone = rootStore.langStore.timeZone;

      // Sincronizar filtros con el store para persistencia en profile
      const gridFilters: GridFilter[] = filters.map(f => ({
        columnName: f.columnName,
        operation: f.operator,
        value: f.value
      }));
      store.grid.setSelectedFilters(gridFilters);

      if (filters.length === 0) {
        store.whereFilters = undefined;
      } else {
        // El filtro se guarda con el nombre de la columna (para el profile) pero
        // se consulta por el campo real: una pseudo-columna del snapshot
        // (`data_.name`) no existe en SQL y filtra por su alias promovido.
        const alias = store.grid.columnFilterAlias;
        const resolved = alias
          ? filters.map(f => (alias[f.columnName] ? { ...f, columnName: alias[f.columnName] } : f))
          : filters;
        const prismaWhere = mrtFiltersToPrismaWhere(resolved, timeZone, store.grid.subFilters);
        // whereFilters espera un array para el spread en loadPage
        (store as any).whereFilters = prismaWhere ? [prismaWhere] : undefined;
      }
      store.debounceLoadFirstPage();
    },
    [store, rootStore.langStore.timeZone]
  );

  const handleSortingChange = useCallback(
    (sorting: { id: string; desc: boolean }[]) => {
      const mapped = sorting.map((s) => ({
        columnName: s.id,
        direction: s.desc ? ('desc' as const) : ('asc' as const),
      }));
      if (mapped.length > 0) {
        store.grid.setSorting(mapped);
        store.loadFirstPage(true);
      }
    },
    [store]
  );

  const handlePaginationChange = useCallback(
    (pagination: { pageIndex: number; pageSize: number }) => {
      if (pagination.pageSize !== store.pageSize) {
        store.onPageSizeChange(pagination.pageSize);
      } else {
        store.onPageChange(pagination.pageIndex + 1);
      }
    },
    [store]
  );

  return {
    handleColumnVisibilityChange,
    handleColumnOrderChange,
    handleColumnSizingChange,
    handleFiltersChange,
    handleSortingChange,
    handlePaginationChange,
  };
}
